import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import LayersDetails from './LayersDetails';
import CustomFieldsService from '../../api/CustomFieldsService';
import CorgeeStorageHelper from '../Globals/CorgeeStorageHelper';
import HeaderDetails from './HeaderDetails';
import RenderInBody from '../Globals/RenderInBody';

import '../../assets/fonts/glyphs/material-icons.css';
import '../../assets/fonts/glyphs/glyphs.css';
import '../../assets/fonts/glyphs/glyphs.css';
import '../../assets/fonts/glyphs-news/glyphs2.css';
import '../Globals/ConversationSummernoteStyles.css';
import './CustomFields.css';
import { useConversationBus } from '../../hooks/useConversationBus';
import { conversationBus } from '../../lib/conversationBus';

// Types
interface CustomField {
  CustomFieldId: number;
  Name: string;
  Value?: any;
  FieldTypeId: number;
  LayerId: number;
  [key: string]: any;
}

interface LayerCustomFields {
  LayerId: number;
  Name: string;
  Color: string;
  IsEnable: boolean;
  IsDefault?: boolean;
  CustomFields: CustomField[];
  [key: string]: any;
}

interface ApiConfig {
  url: string;
  token: string;
}

interface RequestConfig {
  projectId: number | null;
  configValueId: number | null;
  coreTaskId: number | null;
  personId: number | null;
  isDeleted: boolean;
  isAllPhaseResume?: boolean;
  api: ApiConfig;
}

interface Config {
  apiUrl: string;
  authToken: string;
  disableAutoSave?: boolean;
  canEdit?: boolean;
  canTemporary?: boolean;
  isNew?: boolean;
  showHeader?: boolean;
  callApi?: boolean;
  highlight?: boolean;
  hasOwnScope?: boolean;
  SocketConnectionId?: string;
  onLoadDataCallback?: (container: HTMLElement | null, objects: any, functions: any) => void;
  onCustomFieldUpdated?: (field: CustomField) => void;
  UpdateDetailsConversationReact?: (
    fields: LayerCustomFields[],
    conversation: Conversation
  ) => void;
  onRecoverFieldsValues?: (conversation: Conversation) => void;
  [key: string]: any;
}

interface Phase {
  CoreTaskId: number;
  [key: string]: any;
}

interface Conversation {
  ConversationId: number;
  CoreTaskId?: number;
  PersonId?: number;
  ProjectId?: number | null;
  ConfigValueId?: number;
  IsProjectConversation?: boolean;
  layerCustomFields?: LayerCustomFields[];
  LayerCustomFields?: LayerCustomFields[];
  ConversationLayers?: Layer[];
  ConversationGroups?: any[];
  dashboardGroupId?: number;
  OwnedById?: number;
  isAddLayertoCard?: boolean;
  isFromConvertTo?: boolean;
  HasPhases?: boolean;
  Phases?: Phase[];
  [key: string]: any;
}

interface Layer {
  LayerId: number;
  Name?: string;
  LayerName?: string;
  [key: string]: any;
}

interface CustomFieldsProps {
  config: Config;
  item: Conversation;
  configValueId: number | null;
  projectId: number | null;
  groups?: any[] | null;
  section?: string;
  isProjectTitle?: boolean;
  hideImageProject?: boolean;
  containerSelector?: string;
  isProjectView?: boolean;
  isConversationDetailsView?: boolean;
  SocketConnectionId?: string;
  onOpenEditHistory?: (field: CustomField) => void;
  updateCustomFieldsHistory?: (field: CustomField) => void;
  loadingBarStart?: () => void;
  loadingBarComplete?: () => void;
  UpdateCustomFields?: (
    fields: LayerCustomFields[],
    conversation: Conversation,
    updatedField: CustomField
  ) => void;
  getForceReloadDetailsFn?: (fn: Function, config: Config) => void;
  submitTime?: boolean;
  phaseSelected?: Phase;
  isInlineTask?: boolean;
  /* Project is fixed by the surrounding scope (project dashboard): hides the
     Project selector in HeaderDetails so cards can't be created elsewhere. */
  lockProject?: boolean;
}

const CustomFields: React.FC<CustomFieldsProps> = ({
  config,
  item,
  configValueId,
  projectId,
  groups: propGroups = null,
  section = '',
  isProjectTitle = false,
  hideImageProject = false,
  containerSelector: propContainerSelector,
  isProjectView = false,
  isConversationDetailsView = false,
  SocketConnectionId: _SocketConnectionId,
  isInlineTask = false,
  lockProject = false,
  onOpenEditHistory,
  updateCustomFieldsHistory,
  loadingBarStart,
  loadingBarComplete,
  UpdateCustomFields,
  getForceReloadDetailsFn,
  submitTime,
  phaseSelected,
}) =>
{
  const apiConfig = useMemo(() => CorgeeStorageHelper.getApiConfig(config), [config]);
  const canEdit = useMemo(
    () => config.canEdit || config.canTemporary || false,
    [config.canEdit, config.canTemporary],
  );

  const [state, setState] = useState({
    disableAutoSave: config.disableAutoSave || false,
    EditorEnabled: false,
    canEdit,
    isNew: config.isNew || false,
    showHeader: config.showHeader !== undefined ? config.showHeader : true,
    customFieldsbyLayer: [] as LayerCustomFields[],
    config: apiConfig,
    requestConfig: {
      projectId,
      configValueId,
      coreTaskId: item?.HasPhases
        ? (phaseSelected?.CoreTaskId ?? phaseSelected?.CoreTaskID)
        : (item?.CoreTaskId ?? item?.CoreTaskID),
      personId: item?.PersonId || null,
      isDeleted: false,
      isAllPhaseResume:
        item?.HasPhases === true
          ? phaseSelected?.ConversationPhaseId === 0 ||
            phaseSelected?.ConversationPhaseId == null ||
            undefined
          : undefined,
      api: {
        url: apiConfig.apiUrl,
        token: apiConfig.authToken,
      },
    } as RequestConfig,
    initialLoadData: false,
    showDetails: true,
    textDetailsLayers: 'See more details',
    seeMore: config.expandAll || false,
    layersToHide: [] as number[],
    isRecycleBin: window.location.href.indexOf('RecycleBin') > -1,
    projectNameConfig: CorgeeStorageHelper.getProjectNameConfig(),
    containerSelector: propContainerSelector,
    isProjectView: window.location.href.indexOf('ProjectDetails') > -1,
    groups: propGroups,
    highlight: false,
    sLayers: [] as LayerCustomFields[],
    uLayers: [] as LayerCustomFields[],
    conversationUpdated: undefined as Conversation | undefined,
    loadInProgress: false,
  });

  const customFieldsContainerRef = useRef<HTMLDivElement>(null);
  const convHeaderDetailsRef = useRef<any>(null);
  // Unique id per instance to avoid re-applying own bus emissions
  const instanceId = useRef(`cf-${Math.random().toString(36).slice(2)}`);

  // Refs to hold latest callbacks for use inside effects without causing re-triggers
  const getCustomFieldsDataRef = useRef<Function>(() => {});
  // Guards against overlapping, concurrent getCustomFieldsData calls for the
  // identical request - this function has many call sites, so this protects
  // against any combination of them (or React StrictMode's dev-only
  // double-invoke of any single one) firing duplicate network requests
  // before an earlier one resolves.
  const inFlightCustomFieldsKeyRef = useRef<string | null>(null);
  const reloadCustomFieldRef = useRef<Function>(() => {});
  const validateChangesRef = useRef<Function>(() => false);
  const onReloadLayersRef = useRef<Function>(() => {});

  const isValidLayer = useCallback(
    (customFieldLayer: LayerCustomFields, layersArrayToHide: number[]): boolean =>
    {
      if (
        !customFieldLayer.IsEnable ||
        !customFieldLayer.CustomFields ||
        customFieldLayer.CustomFields.length === 0
      )
      {
        return false;
      }

      if (layersArrayToHide && layersArrayToHide.length > 0)
      {
        return !layersArrayToHide.includes(customFieldLayer.LayerId);
      }

      return true;
    },
    [],
  );

  const validateBottomScroll = useCallback(() =>
  {
    if (!customFieldsContainerRef.current) return;

    const obj = $(customFieldsContainerRef.current).closest('.layer-custom-fields-container');
    const top = $(obj).scrollTop() || 0;
    const height =
      ($(customFieldsContainerRef.current).height() || 0) - ($(obj).height() || 0) + 12;

    if (top >= height)
    {
      $(obj).scrollTop(height - 5);
    }
  }, []);

  const validateBottomScrollDashboard = useCallback(() =>
  {
    if (!customFieldsContainerRef.current) return;

    const obj = $(customFieldsContainerRef.current).closest('.sortableDashboard');
    const top = $(obj).scrollTop() || 0;
    const scrollHeight =
      $(customFieldsContainerRef.current).closest('.sortableDashboard')[0]?.scrollHeight || 0;
    const height = scrollHeight - ($(obj).height() || 0) - 12;

    if (top >= height)
    {
      $(obj).scrollTop(height - 5);
    }
  }, []);
  void validateBottomScroll;
  void validateBottomScrollDashboard;

  const sendDataToCallback = useCallback(
    (data: LayerCustomFields[]) =>
    {
      if (!config.onLoadDataCallback) return;

      const dashboardGroupId = item?.dashboardGroupId || null;
      const conversationId = item?.ConversationId || null;

      const reactParamsObjects = {
        data,
        conversationId,
        dashboardGroupIdSelected: dashboardGroupId,
        isProjectTitle,
        hideImageProject,
        hasOwnScope: config.hasOwnScope,
        configValueId,
      };

      const reactParamsFunctions = {
        fncToogleShowDetails: toogleShowDetails,
        fncReloadCustomFields: reloadCustomField,
        fncResetLayersToHide: resetLayersToHide,
        fncUpdateConversation: onUpdateConversation,
        fncChangeContainer: onChangeContainer,
        fncHide: hideDetails,
        fncShow: showDetails,
        fncRecoverFieldsValues: onRecoverFieldsValues,
        fncChangedItemType: onChangedItemType,
        fncUpdateLayersConversation: onUpdateLayersConversation,
        fncUpdateDetails: onUpdateDetails,
      };

      config.onLoadDataCallback(
        customFieldsContainerRef.current,
        reactParamsObjects,
        reactParamsFunctions,
      );
    },
    [config, item, isProjectTitle, hideImageProject, configValueId],
  );

  const onReloadLayers = useCallback(
    (layers: Layer[]) =>
    {
      setState(prev =>
      {
        const customFieldsResponse = prev.customFieldsbyLayer;
        const layerDefault = customFieldsResponse.find(x => x.LayerId === 0);

        const sLayers: LayerCustomFields[] = [];
        const uLayers: LayerCustomFields[] = [];

        if (prev.isNew)
        {
          // For new cards, show all custom-field groups upfront
          customFieldsResponse.forEach(cf => sLayers.push(cf));
        }
        else
        {
          const setLayers = [...layers];
          if (layerDefault)
          {
            setLayers.unshift(layerDefault as any);
          }

          setLayers.forEach(set =>
          {
            const found = customFieldsResponse.find(a => a.LayerId === set.LayerId);
            if (found) sLayers.push(found);
          });

          customFieldsResponse.forEach(item =>
          {
            if (!setLayers.find(x => x.LayerId === item.LayerId))
            {
              uLayers.push(item);
            }
          });

          sLayers.sort((a, b) => a.LayerId - b.LayerId);
          uLayers.sort((a, b) => a.LayerId - b.LayerId);
        }

        return { ...prev, sLayers, uLayers };
      });
    },
    [], // no deps needed â€” reads latest state via prev
  );

  const retrieveCustomDetailsData = useCallback(
    (customFieldsResponse: LayerCustomFields[], highlight?: boolean) =>
    {
      const layerDefault = customFieldsResponse.find(x => x.LayerId === 0);

      setState(prev => ({
        ...prev,
        customFieldsbyLayer: customFieldsResponse,
        initialLoadData: true,
        loadInProgress: false,
      }));

      if (highlight)
      {
        highlightCustomFields();
      }

      sendDataToCallback(customFieldsResponse);

      setTimeout(() =>
      {
        if (config.onRecoverFieldsValues)
        {
          const convCopy = { ...item, layerCustomFields: customFieldsResponse };
          config.onRecoverFieldsValues(convCopy);
        }
      }, 0);

      const sLayers: LayerCustomFields[] = [];
      const uLayers: LayerCustomFields[] = [];

      if (config.isNew)
      {
        // For new cards, show all custom-field groups upfront
        customFieldsResponse.forEach(cf => sLayers.push(cf));
      }
      else
      {
        const setLayers = [...(item.ConversationLayers || [])];
        if (layerDefault)
        {
          setLayers.unshift(layerDefault as any);
        }

        setLayers.forEach(set =>
        {
          const found = customFieldsResponse.find(a => a.LayerId === set.LayerId);
          if (found) sLayers.push(found);
        });

        customFieldsResponse.forEach(item =>
        {
          if (!setLayers.find(x => x.LayerId === item.LayerId))
          {
            uLayers.push(item);
          }
        });

        // Same order as onReloadLayers, so editing the card never reorders the layers
        sLayers.sort((a, b) => a.LayerId - b.LayerId);
        uLayers.sort((a, b) => a.LayerId - b.LayerId);
      }

      setState(prev => ({ ...prev, sLayers, uLayers }));
    },
    [item, config, sendDataToCallback],
  );

  const getCustomFieldsData = useCallback(
    (configParam: Config | { callApi?: boolean }, _isReload?: boolean) =>
    {
      const itemConv = state.conversationUpdated || item;
      const callApi = configParam.callApi !== false;

      if (!callApi || !itemConv) return;

      const { isNew } = state;
      const requestConfig = {
        ...state.requestConfig,
        coreTaskId: itemConv?.HasPhases
          ? phaseSelected?.CoreTaskId
          : (itemConv?.CoreTaskId ?? itemConv?.CoreTaskID),
        personId: itemConv?.PersonId || null,
        projectId: itemConv?.ProjectId || null,
        isAllPhaseResume:
          itemConv?.HasPhases === true
            ? phaseSelected?.ConversationPhaseId === 0 ||
              phaseSelected?.ConversationPhaseId == null ||
              undefined
            : undefined,
      };

      // Guards against overlapping, concurrent calls for the identical
      // request (e.g. React StrictMode's dev-only double-invoke, or multiple
      // of this function's many call sites firing in quick succession for
      // the same underlying need) both hitting the network before either
      // resolves.
      const requestKey = JSON.stringify({
        isNew,
        personId: requestConfig.personId,
        projectId: requestConfig.projectId,
        coreTaskId: requestConfig.coreTaskId,
        conversationId: itemConv.ConversationId,
        isProjectConversation: itemConv.IsProjectConversation,
        isProjectTitle,
        configValueId: requestConfig.configValueId,
      });

      if (inFlightCustomFieldsKeyRef.current === requestKey)
      {
        return;
      }
      inFlightCustomFieldsKeyRef.current = requestKey;

      const clearInFlight = () =>
      {
        if (inFlightCustomFieldsKeyRef.current === requestKey)
        {
          inFlightCustomFieldsKeyRef.current = null;
        }
      };

      let requestPromise: Promise<any> | null = null;

      if (isNew)
      {
        requestPromise = CustomFieldsService.getEmptyCustomFieldsByConfigValueId(requestConfig as any)
          .then(response =>
          {
            retrieveCustomDetailsData(response, config.highlight);
          })
          .catch(error =>
          {
            console.warn('Api Error', error);
          });
      }
      else
      {
        if (itemConv.PersonId && itemConv.PersonId > 0 && itemConv.ConversationId > 0)
        {
          requestPromise = CustomFieldsService.getPersonCustomFieldsByItemId(requestConfig as any)
            .then(response =>
            {
              retrieveCustomDetailsData(response, config.highlight);
            })
            .catch(error =>
            {
              console.warn('Api Error', error);
            });
        }
        else if (
          (itemConv.CoreTaskId ?? itemConv.CoreTaskID) > 0 ||
          itemConv.IsProjectConversation ||
          itemConv.ConversationId > 0
        )
        {
          if (itemConv.IsProjectConversation || isProjectTitle)
          {
            requestPromise = CustomFieldsService.getProjectCustomFieldsByItemId(requestConfig as any)
              .then(response =>
              {
                retrieveCustomDetailsData(response, config.highlight);
              })
              .catch(error =>
              {
                console.warn('Api Error', error);
              });
          }
          else if ((requestConfig.coreTaskId ?? 0) > 0)
          {
            requestPromise = CustomFieldsService.getCustomFieldsByItemId(requestConfig as any)
              .then(response =>
              {
                retrieveCustomDetailsData(response, config.highlight);
              })
              .catch(error =>
              {
                console.warn('Api Error', error);
              });
          }
          // Skip until configValueId is known (arrives async; undefined causes a 400)
          else if (itemConv.ConversationId > 0 && requestConfig.configValueId != null)
          {
            requestPromise = CustomFieldsService.getLayersByConfigValueId(requestConfig as any)
              .then(response =>
              {
                retrieveCustomDetailsData(response, config.highlight);
              })
              .catch(error =>
              {
                console.warn('Api Error', error);
              });
          }
        }
      }

      if (requestPromise)
      {
        requestPromise.finally(clearInFlight);
      }
      else
      {
        // No branch matched (nothing was actually fetched) - clear immediately
        // rather than leaving the guard stuck on a request that never fired.
        clearInFlight();
      }
    },
    [state, item, isProjectTitle, config, phaseSelected, retrieveCustomDetailsData],
  );

  const onChangedItemType = useCallback(
    (newItemType: { ConfigValueId: number; DashboardGroupId?: number }) =>
    {
      setState(prev => ({
        ...prev,
        requestConfig: {
          ...prev.requestConfig,
          configValueId: newItemType.ConfigValueId,
        },
      }));

      if (state.isNew)
      {
        const requestConfigCopy = {
          ...state.requestConfig,
          configValueId: newItemType.ConfigValueId,
        };

        CustomFieldsService.getEmptyCustomFieldsByConfigValueId(requestConfigCopy as any)
          .then(response =>
          {
            retrieveCustomDetailsData(response);

            if (convHeaderDetailsRef.current)
            {
              convHeaderDetailsRef.current.reloadLayers();
            }
          })
          .catch(error =>
          {
            console.warn('Api Error', error);
          });

        if (convHeaderDetailsRef.current)
        {
          convHeaderDetailsRef.current.onChangedItemType(newItemType);
        }
      }
    },
    [state.isNew, state.requestConfig, retrieveCustomDetailsData],
  );

  const onRecoverFieldsValues = useCallback((dataFieldsValues?: LayerCustomFields[]) =>
  {
    if (dataFieldsValues && dataFieldsValues.length > 0)
    {
      setState(prev => ({ ...prev, customFieldsbyLayer: dataFieldsValues }));
    }
  }, []);

  const onChangeContainer = useCallback((containerSelector: string) =>
  {
    setState(prev => ({ ...prev, containerSelector }));
  }, []);

  const onUpdateConversation = useCallback((conversation: Conversation) =>
  {
    setState(prev => ({
      ...prev,
      customFieldsbyLayer: (conversation.layerCustomFields || []) as LayerCustomFields[],
      requestConfig: {
        ...prev.requestConfig,
        projectId: (conversation.ProjectId as any) || null,
        coreTaskId: ((conversation.CoreTaskId ?? conversation.CoreTaskID) as any) || null,
      },
      conversationUpdated: conversation,
      groups: conversation.ConversationGroups as any,
    }));

    if (convHeaderDetailsRef.current)
    {
      convHeaderDetailsRef.current.updateConversation(conversation);
    }
  }, []);

  const onUpdateLayersConversation = useCallback((conversation: Conversation) =>
  {
    setState(prev => ({ ...prev, conversationUpdated: conversation }));

    if (convHeaderDetailsRef.current)
    {
      convHeaderDetailsRef.current.onUpdateLayersConversation(
        conversation.ConversationLayers || [],
      );
    }
  }, []);

  const onUpdateDetails = useCallback((layerCustomFields: LayerCustomFields[]) =>
  {
    setState(prev =>
    {
      const conversationUpdatedCopy = {
        ...prev.conversationUpdated,
        layerCustomFields,
      } as Conversation;

      return {
        ...prev,
        customFieldsbyLayer: layerCustomFields,
        conversationUpdated: conversationUpdatedCopy,
      };
    });
  }, []);

  const highlightCustomFields = useCallback(() =>
  {
    setState(prev => ({ ...prev, highlight: true }));
    setTimeout(() =>
    {
      setState(prev => ({ ...prev, highlight: false }));
    }, 2000);
  }, []);

  const validateChanges = useCallback(
    (nextProps: CustomFieldsProps): boolean =>
    {
      if (!state.customFieldsbyLayer) return false;

      const nextLayerCustomFields = nextProps.item.layerCustomFields;
      if (!nextLayerCustomFields) return false;

      if (nextLayerCustomFields.length !== state.customFieldsbyLayer.length)
      {
        return true;
      }

      for (const nextLayer of nextLayerCustomFields)
      {
        for (const layer of state.customFieldsbyLayer)
        {
          if (
            layer &&
            nextLayer &&
            layer.LayerId === nextLayer.LayerId &&
            JSON.stringify(layer) !== JSON.stringify(nextLayer)
          )
          {
            return true;
          }
        }
      }

      return false;
    },
    [state.customFieldsbyLayer],
  );

  const reloadCustomField = useCallback(
    (resetRequestState?: boolean) =>
    {
      setTimeout(() =>
      {
        if (resetRequestState)
        {
          setState(prev => ({
            ...prev,
            requestConfig: {
              ...prev.requestConfig,
              projectId,
              configValueId,
              coreTaskId: (item?.CoreTaskId ?? item?.CoreTaskID) || null,
              personId: item?.PersonId || null,
            },
          }));
        }

        setTimeout(() =>
        {
          getCustomFieldsData(config, true);
        }, 0);
      }, 0);
    },
    [projectId, configValueId, item, config, getCustomFieldsData],
  );

  const UpdateCustomFieldsByLayer = useCallback(
    (layerCustomFieldsUpdated: LayerCustomFields, updatedCustomField: CustomField) =>
    {
      setState(prev =>
      {
        const indexLayer = prev.customFieldsbyLayer.findIndex(
          layerItem => layerItem.LayerId === layerCustomFieldsUpdated.LayerId,
        );

        const newCustomFields = [...prev.customFieldsbyLayer];
        if (indexLayer >= 0)
        {
          newCustomFields[indexLayer] = layerCustomFieldsUpdated;
        }

        const updatedLayerId = layerCustomFieldsUpdated.LayerId;
        const newSLayers = prev.sLayers.map(l =>
          l.LayerId === updatedLayerId ? layerCustomFieldsUpdated : l,
        );
        const newULayers = prev.uLayers.map(l =>
          l.LayerId === updatedLayerId ? layerCustomFieldsUpdated : l,
        );

        return {
          ...prev,
          customFieldsbyLayer: newCustomFields,
          sLayers: newSLayers,
          uLayers: newULayers,
        };
      });

      // When the Photo field in Layer 0 (transparent) is updated, notify other components
      // Only emit bus events when NOT in disableAutoSave mode (e.g. EditCard modal)
      if (!state.disableAutoSave)
      {
        if (layerCustomFieldsUpdated.LayerId === 0 && updatedCustomField.FieldTypeId === 10)
        {
          const imageUrl = (updatedCustomField as any).binary;
          if (imageUrl && item?.ConversationId)
          {
            conversationBus.emit('image-updated', {
              conversationId: item.ConversationId,
              imageUrl,
            });
          }
        }

        // Notify other instances (InfoWindow, etc.) to update and auto-switch phase if needed
        if (item?.ConversationId)
        {
          conversationBus.emit('layer-updated', {
            conversationId: item.ConversationId,
            layerId: layerCustomFieldsUpdated.LayerId,
            layerData: layerCustomFieldsUpdated as any,
            sourceId: instanceId.current, // 'cf-*' identifies CustomFields source
            skipInfoWindowReload: true,
            coreTaskId: phaseSelected?.CoreTaskId ?? phaseSelected?.CoreTaskID,
            conversationPhaseId: phaseSelected?.ConversationPhaseId,
          });
        }
      }

      // Build the updated custom fields array to include the current change
      // (state.customFieldsbyLayer in the closure is stale â€” it misses this update)
      const updatedCustomFields = [...state.customFieldsbyLayer];
      const updateIdx = updatedCustomFields.findIndex(
        l => l.LayerId === layerCustomFieldsUpdated.LayerId,
      );
      if (updateIdx >= 0)
      {
        updatedCustomFields[updateIdx] = layerCustomFieldsUpdated;
      }

      setTimeout(() =>
      {
        if (config.UpdateDetailsConversationReact)
        {
          const conversationValidated = { ...item };
          if (conversationValidated.ConfigValueId === 9)
          {
            conversationValidated.ConversationGroups = state.groups || undefined;
          }

          config.UpdateDetailsConversationReact(updatedCustomFields, conversationValidated);
        }

        if (UpdateCustomFields)
        {
          const conversationValidated = { ...item };
          if (conversationValidated.ConfigValueId === 9)
          {
            conversationValidated.ConversationGroups = state.groups || undefined;
          }

          UpdateCustomFields(updatedCustomFields, conversationValidated, updatedCustomField);
        }
      }, 0);
    },
    [
      config,
      item,
      state.groups,
      state.customFieldsbyLayer,
      state.disableAutoSave,
      UpdateCustomFields,
    ],
  );

  const toogleShowDetails = useCallback(
    (callApi = true) =>
    {
      let showDetailsValue = !state.showDetails;

      if (isProjectView)
      {
        showDetailsValue = true;

        const containerElement = $('#canvasDetails');
        if (containerElement.length > 0 && !containerElement[0]!.hasAttribute('data-is-react'))
        {
          containerElement.attr('data-is-react', 'true');
          setState(prev => ({ ...prev, containerSelector: '#canvasDetails' }));
        }
      }
      else
      {
        const containerElement = $(
          `li[data-conversationid=${item.ConversationId}] .show-details-panel`,
        );
        if (
          containerElement.length > 0 &&
          !containerElement[0]!.hasAttribute('data-is-react') &&
          showDetailsValue
        )
        {
          containerElement.attr('data-is-react', 'true');
          setState(prev => ({
            ...prev,
            containerSelector: `li[data-conversationid='${item.ConversationId}'] .show-details-panel`,
          }));
        }

        if (callApi)
        {
          setState(prev => ({ ...prev, showDetails: showDetailsValue }));
        }
      }

      if (showDetailsValue)
      {
        setTimeout(() =>
        {
          getCustomFieldsData({ callApi }, true);
        }, 0);
      }
    },
    [state.showDetails, isProjectView, item, getCustomFieldsData],
  );

  const hideDetails = useCallback(() =>
  {
    setState(prev => ({ ...prev, showDetails: false }));
  }, []);

  const showDetails = useCallback(() =>
  {
    const containerElement = $(
      `li[data-conversationid=${item.ConversationId}] .show-details-panel`,
    );
    if (containerElement.length > 0 && !containerElement[0]!.hasAttribute('data-is-react'))
    {
      containerElement.attr('data-is-react', 'true');
      setState(prev => ({
        ...prev,
        containerSelector: `li[data-conversationid='${item.ConversationId}'] .show-details-panel`,
      }));
    }
    setState(prev => ({ ...prev, showDetails: true }));
  }, [item]);

  const setSeeMore = useCallback(() =>
  {
    setState(prev => ({
      ...prev,
      seeMore: !prev.seeMore,
      textDetailsLayers: !prev.seeMore ? 'See less details' : 'See more details',
    }));
  }, []);

  const resetLayersToHide = useCallback(() =>
  {
    setState(prev => ({
      ...prev,
      layersToHide: CorgeeStorageHelper.getLayersToHide() as any,
    }));
  }, []);

  // Effects
  useEffect(() =>
  {
    if (item && config && config.callApi === false)
    {
      const layerCustomFieldsList = item.layerCustomFields || item.LayerCustomFields || [];

      const data = layerCustomFieldsList.filter(x => x !== undefined && x !== null);

      retrieveCustomDetailsData(data);
    }

    setState(prev => ({
      ...prev,
      layersToHide: CorgeeStorageHelper.getLayersToHide() as any,
    }));
  }, []);

  useEffect(() =>
  {
    if (CorgeeStorageHelper.isAuthenticated(config))
    {
      setTimeout(() =>
      {
        getCustomFieldsData(config, true);

        if (getForceReloadDetailsFn && typeof getForceReloadDetailsFn === 'function')
        {
          getForceReloadDetailsFn(getCustomFieldsData, config);
        }
      }, 0);
    }
    else
    {
      console.error('CustomFields React Component: You have not authenticated.');
    }
  }, []);

  // Keep callback refs up to date (runs on every render, no deps needed)
  useEffect(() =>
  {
    getCustomFieldsDataRef.current = getCustomFieldsData;
    reloadCustomFieldRef.current = reloadCustomField;
    validateChangesRef.current = validateChanges;
    onReloadLayersRef.current = onReloadLayers;
  });

  // Re-fetch custom fields when SubmitTime submits for this conversation
  useEffect(() =>
  {
    const convId = item?.ConversationId;
    if (!convId) return;
    const unsubAdd = conversationBus.on('reply-added', data =>
    {
      if (data.conversationId !== convId) return;
      setTimeout(() =>
      {
        getCustomFieldsDataRef.current(state.config, true);
      }, 0);
    });
    const unsubUpdate = conversationBus.on('reply-updated', data =>
    {
      if (data.conversationId !== convId) return;
      setTimeout(() =>
      {
        getCustomFieldsDataRef.current(state.config, true);
      }, 0);
    });
    return () =>
    {
      unsubAdd();
      unsubUpdate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.ConversationId]);

  useEffect(() =>
  {
    // NOTE: layersToHide is already set in initial mount useEffect, no need to update here

    // Don't mutate item prop - use filtered layers only where needed
    const filteredConversationLayers =
      item?.ConversationLayers && item.ConversationLayers.length > 0
        ? item.ConversationLayers.filter(x => x.LayerId !== 0)
        : [];

    if (
      submitTime &&
      item?.layerCustomFields &&
      item.layerCustomFields.length > 0 &&
      validateChangesRef.current({ item } as any)
    )
    {
      setState(prev => ({ ...prev, customFieldsbyLayer: item.layerCustomFields! }));
      onReloadLayersRef.current(filteredConversationLayers);
    }

    if ((!state.groups || state.groups.length === 0) && propGroups && propGroups.length > 0)
    {
      setState(prev => ({ ...prev, groups: propGroups }));
    }

    // Sync projectId/configValueId into requestConfig and refetch. configValueId
    // arrives async on linked/detail conversations, so it must be tracked here.
    const projectChanged = projectId !== state.requestConfig.projectId;
    const configValueChanged =
      configValueId != null && configValueId !== state.requestConfig.configValueId;

    if ((projectChanged || configValueChanged) && state.showDetails)
    {
      setState(prev => ({
        ...prev,
        requestConfig: {
          ...prev.requestConfig,
          projectId,
          configValueId,
        },
      }));

      setTimeout(() =>
      {
        getCustomFieldsDataRef.current(config, true);
      }, 0);
    }

    if (item?.isAddLayertoCard)
    {
      onUpdateLayersConversation(item);
    }

    if (item?.isFromConvertTo)
    {
      onUpdateConversation(item);
      onUpdateLayersConversation(item);
      reloadCustomFieldRef.current(true);
    }

    if (submitTime)
    {
      setTimeout(() =>
      {
        getCustomFieldsDataRef.current(config, true);
      }, 0);
    }
    // Only the `item` fields this effect reads, so unrelated host edits don't re-run it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    item?.ConversationLayers,
    item?.layerCustomFields,
    item?.isAddLayertoCard,
    item?.isFromConvertTo,
    submitTime,
    propGroups,
    projectId,
    configValueId,
    state.groups,
    state.requestConfig.projectId,
    state.requestConfig.configValueId,
    state.showDetails,
    onUpdateLayersConversation,
    onUpdateConversation,
  ]);

  // Refetch custom fields specifically when the selected phase changes
  useEffect(() =>
  {
    const newCoreTaskId = phaseSelected?.CoreTaskId ?? phaseSelected?.CoreTaskID;
    if (newCoreTaskId && newCoreTaskId !== state.requestConfig.coreTaskId)
    {
      setState(prev =>
      {
        if (prev.requestConfig.coreTaskId === newCoreTaskId)
        {
          return prev;
        }
        return {
          ...prev,
          requestConfig: {
            ...prev.requestConfig,
            coreTaskId: newCoreTaskId,
            isAllPhaseResume:
              item?.HasPhases === true
                ? phaseSelected?.ConversationPhaseId === 0 ||
                  phaseSelected?.ConversationPhaseId == null ||
                  undefined
                : undefined,
          },
          sLayers: [],
          uLayers: [],
          customFieldsbyLayer: [],
        };
      });
      setTimeout(() =>
      {
        getCustomFieldsDataRef.current(config, true);
      }, 50);
    }
  }, [phaseSelected, config, state.requestConfig.coreTaskId]);

  useConversationBus('image-updated', item?.ConversationId, data =>
  {
    setState(prev =>
    {
      const customFields = [...prev.customFieldsbyLayer];
      const layerZeroIdx = customFields.findIndex(l => l.LayerId === 0);
      let updatedLayer: LayerCustomFields | null = null;

      if (layerZeroIdx >= 0)
      {
        const layer = { ...customFields[layerZeroIdx] } as LayerCustomFields;
        if (layer.CustomFields)
        {
          layer.CustomFields = layer.CustomFields.map((f: any) =>
            f.Description === 'Photo' ? { ...f, binary: data.imageUrl } : f,
          );
          customFields[layerZeroIdx] = layer;
          updatedLayer = layer;
        }
      }

      if (!updatedLayer) return prev;

      // Also sync sLayers/uLayers â€” these are what the render iterates over
      const sLayers = prev.sLayers.map(l => (l.LayerId === 0 ? updatedLayer! : l));
      const uLayers = prev.uLayers.map(l => (l.LayerId === 0 ? updatedLayer! : l));

      const newConvUpdated = prev.conversationUpdated ? { ...prev.conversationUpdated } : undefined;

      if (newConvUpdated && newConvUpdated.layerCustomFields)
      {
        const newConvLayers = [...newConvUpdated.layerCustomFields];
        const convLayerZeroIdx = newConvLayers.findIndex(l => l.LayerId === 0);
        if (convLayerZeroIdx >= 0)
        {
          const layer = { ...newConvLayers[convLayerZeroIdx] } as LayerCustomFields;
          if (layer.CustomFields)
          {
            layer.CustomFields = layer.CustomFields.map((f: any) =>
              f.Description === 'Photo' ? { ...f, binary: data.imageUrl } : f,
            );
            newConvLayers[convLayerZeroIdx] = layer;
          }
        }
        newConvUpdated.layerCustomFields = newConvLayers;
      }

      return {
        ...prev,
        customFieldsbyLayer: customFields,
        sLayers,
        uLayers,
        conversationUpdated: newConvUpdated as any,
      };
    });
  });

  useConversationBus('layer-updated', item?.ConversationId, data =>
  {
    // Skip updates emitted by this same instance
    if (data.sourceId === instanceId.current) return;

    setState(prev =>
    {
      const inSLayers = prev.sLayers.some(l => l.LayerId === data.layerId);
      const inULayers = prev.uLayers.some(l => l.LayerId === data.layerId);
      if (!inSLayers && !inULayers) return prev;

      const newLayer = data.layerData as LayerCustomFields;
      return {
        ...prev,
        customFieldsbyLayer: prev.customFieldsbyLayer.map(l =>
          l.LayerId === data.layerId ? newLayer : l,
        ),
        sLayers: prev.sLayers.map(l => (l.LayerId === data.layerId ? newLayer : l)),
        uLayers: prev.uLayers.map(l => (l.LayerId === data.layerId ? newLayer : l)),
      };
    });
  });

  // When EditCard saves and emits conversation-updated, reload custom fields from the API.
  // A card being created has no id, so the hook listens to every conversation; those events
  // belong to other cards and refetching would wipe the values typed into the create modal.
  useConversationBus('conversation-updated', item?.ConversationId, data =>
  {
    if (!item?.ConversationId) return;
    if ((data as any).updates?.refreshCustomFields)
    {
      setTimeout(() =>
      {
        getCustomFieldsDataRef.current(config, true);
      }, 0);
    }
  });

  // When a field is updated externally (e.g. GridView cell edit / paste / drag-fill), reload.
  // Subscribe globally (null) so we can also match by phase CoreTaskId, not just conversationId.
  useConversationBus('field-updated', null, data =>
  {
    if (!item) return;

    const matchesDirect = data.conversationId === item.ConversationId;
    const matchesPhase =
      !matchesDirect &&
      item.HasPhases &&
      Array.isArray(item.Phases) &&
      item.Phases.some((p: any) =>
      {
        const pCoreTaskId = p.CoreTaskId ?? p.CoreTaskID;
        return (
          p.ConversationId === data.conversationId ||
          (data.coreTaskId && pCoreTaskId && pCoreTaskId === data.coreTaskId) ||
          (pCoreTaskId && pCoreTaskId === data.conversationId)
        );
      });

    if (matchesDirect || matchesPhase)
    {
      setTimeout(() =>
      {
        getCustomFieldsDataRef.current(config, true);
      }, 0);
    }
  });

  if (!item)
  {
    return null;
  }

  const onCustomFieldUpdated = config.onCustomFieldUpdated || null;
  const showHeaderValidated = isProjectTitle ? false : state.showHeader;

  if (state.containerSelector && state.showDetails)
  {
    return (
      <RenderInBody>
        <div
          data-is-react="true"
          ref={customFieldsContainerRef}
          className={`${
            state.showDetails
              ? 'corgee_com_custom-fields-container details-tab-animation-content layer-details-container react-item-highlighted'
              : 'show-details-panel'
          }${state.highlight ? ' react-item-highlighted' : ''}`}
        >
          {state.showDetails &&
            showHeaderValidated &&
            React.createElement(HeaderDetails as any, {
              config,
              item,
              configValueId,
              projectId,
              groups: state.groups || undefined,
              section,
              isProjectTitle,
              hideImageProject,
              isProjectView,
              isConversationDetailsView,
              lockProject,
              configData: state.config,
              isNew: state.isNew,
              disableAutoSave: state.disableAutoSave,
              keepOneLayer: isInlineTask,
              sendDataToCallback,
              onUpdateConversationGroups: onUpdateConversation,
              onReloadLayers,
            })}
          {state.showDetails &&
            state.customFieldsbyLayer.map(customField =>
              customField &&
              customField.IsEnable &&
              isValidLayer(customField, state.layersToHide) ? (
                  <LayerItem
                    key={`l-cf-${customField.LayerId}`}
                    conversation={item}
                    disableAutoSave={state.disableAutoSave}
                    canEdit={state.canEdit}
                    customField={customField}
                    configValueId={state.requestConfig.configValueId!}
                    projectId={projectId}
                    apiConfig={state.requestConfig.api}
                    EditorEnabled={state.EditorEnabled}
                    UpdateCustomFieldsByLayer={UpdateCustomFieldsByLayer}
                    groups={state.groups || undefined}
                    isProjectTitle={isProjectTitle}
                    hideImageProject={hideImageProject}
                    onCustomFieldUpdated={onCustomFieldUpdated}
                    SocketConnectionId={config.SocketConnectionId}
                    onOpenEditHistory={onOpenEditHistory}
                    updateCustomFieldsHistory={updateCustomFieldsHistory}
                    loadingBarStart={loadingBarStart}
                    loadingBarComplete={loadingBarComplete}
                    phaseSelected={phaseSelected}
                  />
                ) : null,
            )}
        </div>
      </RenderInBody>
    );
  }

  const firstValidLayerIndex = isInlineTask
    ? state.sLayers.findIndex(
      cf => cf && cf.IsEnable && isValidLayer(cf, state.layersToHide),
    )
    : -1;
  const firstValidLayer =
    firstValidLayerIndex >= 0 ? state.sLayers[firstValidLayerIndex] : null;

  // Mirror LayerItem's own field filtering (isProjectTitle / hideImageProject)
  // since the merged-first-layer path below bypasses LayerItem entirely.
  const firstLayerFiltered = firstValidLayer
    ? (() =>
    {
      const copy = { ...firstValidLayer };
      let filteredFields = [...firstValidLayer.CustomFields];
      if (isProjectTitle)
      {
        filteredFields = firstValidLayer.CustomFields.filter(
          f => f.FieldTypeId === 6 || f.FieldTypeId === 7,
        );
      }
      if (hideImageProject && firstValidLayer.IsDefault)
      {
        filteredFields = filteredFields.filter(f => f.Name !== 'Photo');
      }
      copy.CustomFields = filteredFields;
      return copy;
    })()
    : null;

  const canMergeFirstSection =
    isInlineTask && showHeaderValidated && firstLayerFiltered && firstLayerFiltered.CustomFields.length > 0;

  return (
    <div
      data-is-react="true"
      ref={customFieldsContainerRef}
      className={`${
        state.showDetails
          ? 'corgee_com_custom-fields-container details-tab-animation-content layer-details-container'
          : 'show-details-panel'
      }${state.highlight ? ' react-item-highlighted' : ''}`}
    >
      {state.showDetails && canMergeFirstSection ? (
        <div className="corgee_com_custom-fields-color-wrapper _DEL_color-wrapper no-layer">
          <ul className="_DEL_inner">
            {React.createElement(HeaderDetails as any, {
              config,
              item,
              configValueId,
              projectId,
              groups: state.groups || undefined,
              section,
              isProjectTitle,
              hideImageProject,
              isProjectView,
              isConversationDetailsView,
              lockProject,
              configData: state.config,
              isNew: state.isNew,
              disableAutoSave: state.disableAutoSave,
              keepOneLayer: isInlineTask,
              ref: convHeaderDetailsRef,
              sendDataToCallback,
              onUpdateConversationGroups: onUpdateConversation,
              onReloadLayers,
              fieldsOnly: true,
            })}
            <LayersDetails
              conversation={item as any}
              filterDetailsAllPhases={false}
              disableAutoSave={state.disableAutoSave}
              canEdit={state.canEdit}
              customField={firstLayerFiltered as any}
              apiConfig={state.requestConfig.api}
              EditorEnabled={state.EditorEnabled}
              configValueId={state.requestConfig.configValueId! as any}
              projectId={projectId as any}
              UpdateCustomFieldsByLayer={UpdateCustomFieldsByLayer as any}
              groups={state.groups || undefined}
              onCustomFieldUpdated={onCustomFieldUpdated as any}
              isConversationDetailsView={isConversationDetailsView}
              SocketConnectionId={config.SocketConnectionId}
              onOpenEditHistory={onOpenEditHistory as any}
              updateCustomFieldsHistory={updateCustomFieldsHistory as any}
              loadingBarStart={loadingBarStart}
              loadingBarComplete={loadingBarComplete}
              wrapInUl={false}
            />
          </ul>
        </div>
      ) : (
        state.showDetails &&
        showHeaderValidated &&
        React.createElement(HeaderDetails as any, {
          config,
          item,
          configValueId,
          projectId,
          groups: state.groups || undefined,
          section,
          isProjectTitle,
          hideImageProject,
          isProjectView,
          isConversationDetailsView,
          lockProject,
          configData: state.config,
          isNew: state.isNew,
          disableAutoSave: state.disableAutoSave,
          keepOneLayer: isInlineTask,
          ref: convHeaderDetailsRef,
          sendDataToCallback,
          onUpdateConversationGroups: onUpdateConversation,
          onReloadLayers,
        })
      )}
      {state.showDetails &&
        state.sLayers.map((customField, index) =>
          canMergeFirstSection && index === firstValidLayerIndex ? null :
            customField && customField.IsEnable && isValidLayer(customField, state.layersToHide) ? (
              <LayerItem
                key={`l-cf-${customField.LayerId}`}
                conversation={item}
                disableAutoSave={state.disableAutoSave}
                canEdit={state.canEdit}
                customField={customField}
                configValueId={state.requestConfig.configValueId!}
                projectId={projectId}
                apiConfig={state.requestConfig.api}
                EditorEnabled={state.EditorEnabled}
                UpdateCustomFieldsByLayer={UpdateCustomFieldsByLayer}
                groups={state.groups || undefined}
                isProjectTitle={isProjectTitle}
                hideImageProject={hideImageProject}
                onCustomFieldUpdated={onCustomFieldUpdated}
                isConversationDetailsView={isConversationDetailsView}
                SocketConnectionId={config.SocketConnectionId}
                onOpenEditHistory={onOpenEditHistory}
                updateCustomFieldsHistory={updateCustomFieldsHistory}
                loadingBarStart={loadingBarStart}
                loadingBarComplete={loadingBarComplete}
                phaseSelected={phaseSelected}
              />
            ) : null,
        )}
      {state.seeMore &&
        state.uLayers.map(customField =>
          customField && customField.IsEnable && isValidLayer(customField, state.layersToHide) ? (
            <LayerItem
              key={`l-cf-${customField.LayerId}`}
              conversation={item}
              disableAutoSave={state.disableAutoSave}
              canEdit={state.canEdit}
              customField={customField}
              configValueId={state.requestConfig.configValueId!}
              projectId={projectId}
              apiConfig={state.requestConfig.api}
              EditorEnabled={state.EditorEnabled}
              UpdateCustomFieldsByLayer={UpdateCustomFieldsByLayer}
              groups={state.groups || undefined}
              isProjectTitle={isProjectTitle}
              hideImageProject={hideImageProject}
              onCustomFieldUpdated={onCustomFieldUpdated}
              isConversationDetailsView={isConversationDetailsView}
              SocketConnectionId={config.SocketConnectionId}
              onOpenEditHistory={onOpenEditHistory}
              updateCustomFieldsHistory={updateCustomFieldsHistory}
              loadingBarStart={loadingBarStart}
              loadingBarComplete={loadingBarComplete}
              phaseSelected={phaseSelected}
            />
          ) : null,
        )}
      {!config.expandAll && !state.isNew && (
        <div className="corgee_com_card-see-more-details">
          <a className="corgee_com_card-hide-details-btn" onClick={setSeeMore}>
            {state.textDetailsLayers}
          </a>
        </div>
      )}
    </div>
  );
};

export default CustomFields;

// LayerItem Component
interface LayerItemProps {
  disableAutoSave: boolean;
  canEdit: boolean;
  customField: LayerCustomFields;
  configValueId: number;
  projectId: number | null;
  apiConfig: ApiConfig;
  EditorEnabled: boolean;
  UpdateCustomFieldsByLayer: (
    layerCustomFields: LayerCustomFields,
    updatedField: CustomField
  ) => void;
  groups?: any[];
  isProjectTitle?: boolean;
  hideImageProject?: boolean;
  onCustomFieldUpdated?: ((field: CustomField) => void) | null;
  isConversationDetailsView?: boolean;
  SocketConnectionId?: string;
  onOpenEditHistory?: ((field: CustomField) => void) | undefined;
  updateCustomFieldsHistory?: ((field: CustomField) => void) | undefined;
  loadingBarStart?: (() => void) | undefined;
  loadingBarComplete?: (() => void) | undefined;
  conversation: Conversation;
  phaseSelected?: Phase;
}

const LayerItem: React.FC<LayerItemProps> = ({
  disableAutoSave,
  canEdit,
  customField,
  configValueId,
  projectId,
  apiConfig,
  EditorEnabled,
  UpdateCustomFieldsByLayer,
  groups,
  isProjectTitle,
  hideImageProject,
  onCustomFieldUpdated,
  isConversationDetailsView,
  SocketConnectionId,
  onOpenEditHistory,
  updateCustomFieldsHistory,
  loadingBarStart,
  loadingBarComplete,
  conversation,
  phaseSelected,
}) =>
{
  const copyCustomField = useMemo(() =>
  {
    const copy = { ...customField };
    let filteredFields = [...customField.CustomFields];

    if (isProjectTitle)
    {
      filteredFields = customField.CustomFields.filter(
        item => item.FieldTypeId === 6 || item.FieldTypeId === 7,
      );
    }

    if (hideImageProject && customField.IsDefault)
    {
      filteredFields = filteredFields.filter(item => item.Name !== 'Photo');
    }

    copy.CustomFields = filteredFields;
    return copy;
  }, [customField, isProjectTitle, hideImageProject]);

  const showLayer = useMemo(() =>
  {
    if (!phaseSelected || !conversation.HasPhases || !conversation.Phases)
    {
      return true;
    }

    const filterHasPhases =
      conversation.Phases.filter(x => phaseSelected.CoreTaskId === x.CoreTaskId).length === 0 &&
      conversation.HasPhases;

    if (filterHasPhases && copyCustomField.CustomFields[0]?.LayerId !== 0)
    {
      return copyCustomField.CustomFields.filter(x => x.Value != null).length > 0;
    }

    return true;
  }, [conversation, phaseSelected, copyCustomField]);

  if (copyCustomField.CustomFields.length === 0 || !showLayer)
  {
    return null;
  }

  return (
    <div className="corgee_com_custom-fields-color-wrapper _DEL_color-wrapper">
      <div
        className="corgee_com_custom-field-top-border _DEL_top-border enable-help-tooltip layer-details-tooltip"
        data-title={customField.Name}
        style={{ borderColor: customField.Color }}
      />
      <LayersDetails
        conversation={conversation as any}
        filterDetailsAllPhases={false}
        disableAutoSave={disableAutoSave}
        canEdit={canEdit}
        customField={copyCustomField as any}
        apiConfig={apiConfig}
        EditorEnabled={EditorEnabled}
        configValueId={configValueId as any}
        projectId={projectId as any}
        UpdateCustomFieldsByLayer={UpdateCustomFieldsByLayer as any}
        groups={groups}
        onCustomFieldUpdated={onCustomFieldUpdated as any}
        isConversationDetailsView={isConversationDetailsView}
        SocketConnectionId={SocketConnectionId}
        onOpenEditHistory={onOpenEditHistory as any}
        updateCustomFieldsHistory={updateCustomFieldsHistory as any}
        loadingBarStart={loadingBarStart}
        loadingBarComplete={loadingBarComplete}
      />
    </div>
  );
};
